import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    RemoveAccountParentRelationship as RemoveAccountParentRelationshipMutation,
    RemoveAccountParentRelationshipVariables as RemoveAccountParentRelationshipMutationVariables,
    AccountParentRemoveInput,
} from 'nutshell-graphql-types';

import ACCOUNT_HIERARCHY_INFO_FRAGMENT from '../fragments/account-hierarchy-info-fragment.graphql';
import REMOVE_ACCOUNT_PARENT_RELATIONSHIP from '../mutations/remove-account-parent-relationship.graphql';

export const useRemoveAccountParentRelationship = (
    currentAccountId: string
): {
    removeAccountParentRelationship: (input: AccountParentRemoveInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [removeAccountParentRelationship, {loading, error}] = useMutation<
        RemoveAccountParentRelationshipMutation,
        RemoveAccountParentRelationshipMutationVariables
    >(REMOVE_ACCOUNT_PARENT_RELATIONSHIP);

    return {
        removeAccountParentRelationship: (input: AccountParentRemoveInput) => {
            return removeAccountParentRelationship({
                variables: {input},
                update: (cache, mutationResults) => {
                    const payload =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.accountParentRemove;

                    if (!payload) {
                        return;
                    }

                    // Store the parent or child account being returned based on which one matches the currentAccountId
                    let accountToUpdate = null;
                    if (payload.account?.id === currentAccountId) {
                        accountToUpdate = payload.account;
                    } else if (payload.parentAccount?.id === currentAccountId) {
                        accountToUpdate = payload.parentAccount;
                    }

                    if (!accountToUpdate) {
                        return;
                    }

                    cache.writeFragment({
                        id: currentAccountId,
                        fragment: ACCOUNT_HIERARCHY_INFO_FRAGMENT,
                        fragmentName: 'AccountHierarchyInfoFragment',
                        data: accountToUpdate,
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
